import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAdHistory } from '../../hooks/useAdHistory';
import { useCreditsContext } from '../../context/CreditsContext';
import { getEdgeFunctionHeaders } from '../../lib/edgeFunctions';
import { getSupabaseFunctionUrl } from '../../lib/supabase';
import { downloadFile } from '../../lib/download';
import { exportVideoWithOverlays } from '../../lib/exportVideoWithOverlays';
import { VIDEO_AD_MUSIC_CREDITS, VIDEO_AD_SOUND_EFFECT_CREDITS, VIDEO_AD_VOICEOVER_CREDITS, type VideoAdDuration } from '../../config/videoAd';
import type { VideoAspectRatio } from '../video-editor/types';

type AudioMode = 'voiceover' | 'sound_effect' | 'music';
type VoiceStyle = 'friendly' | 'energetic' | 'luxury' | 'calm' | 'bold';
type SoundEffectStyle = 'whoosh' | 'pop' | 'cash' | 'camera' | 'crowd' | 'arcade' | 'chime' | 'custom';
type MusicStyle = 'upbeat' | 'cinematic' | 'luxury' | 'retro' | 'chill' | 'dramatic' | 'custom';

interface AudioStudioState {
  videoUrl?: string;
  audioUrl?: string | null;
  voiceoverScript?: string | null;
  generationId?: string | null;
  businessName?: string | null;
  businessType?: string | null;
  offer?: string | null;
  platform?: string | null;
  audience?: string | null;
  tone?: string | null;
  aspectRatio?: VideoAspectRatio;
}

const AUDIO_EDGE_URL = getSupabaseFunctionUrl('generate-video-ad');

const soundEffectOptions: { id: SoundEffectStyle; label: string; prompt: string; icon: string }[] = [
  { id: 'whoosh', label: 'Whoosh', prompt: 'Fast clean cinematic whoosh transition with subtle impact', icon: 'ri-windy-line' },
  { id: 'pop', label: 'Pop', prompt: 'Short upbeat pop accent for a social media reveal', icon: 'ri-bubble-chart-line' },
  { id: 'cash', label: 'Cash', prompt: 'Bright cash register ding for a retail sale or offer', icon: 'ri-money-dollar-circle-line' },
  { id: 'camera', label: 'Camera', prompt: 'Polished camera shutter click for a product reveal', icon: 'ri-camera-line' },
  { id: 'crowd', label: 'Crowd', prompt: 'Small crowd cheer and upbeat applause swell', icon: 'ri-user-smile-line' },
  { id: 'arcade', label: 'Arcade', prompt: 'Retro arcade coin pickup and playful 8-bit flourish', icon: 'ri-gamepad-line' },
  { id: 'chime', label: 'Chime', prompt: 'Soft premium notification chime with warm sparkle', icon: 'ri-notification-3-line' },
  { id: 'custom', label: 'Custom', prompt: '', icon: 'ri-edit-2-line' },
];

const musicOptions: { id: MusicStyle; label: string; prompt: string; icon: string }[] = [
  { id: 'upbeat', label: 'Upbeat', prompt: 'Upbeat modern instrumental ad music with bright drums, warm bass, hand claps, and optimistic energy. No vocals or lyrics.', icon: 'ri-sparkling-line' },
  { id: 'cinematic', label: 'Cinematic', prompt: 'Cinematic instrumental background score with confident percussion, subtle strings, and a polished brand reveal feel. No vocals or lyrics.', icon: 'ri-movie-2-line' },
  { id: 'luxury', label: 'Luxury', prompt: 'Premium instrumental music bed with elegant piano, soft synth pads, restrained percussion, and refined high-end atmosphere. No vocals or lyrics.', icon: 'ri-vip-diamond-line' },
  { id: 'retro', label: 'Retro', prompt: 'Fun retro-inspired instrumental ad music with vintage synths, punchy drums, and playful nostalgic energy. No vocals or lyrics.', icon: 'ri-cassette-line' },
  { id: 'chill', label: 'Chill', prompt: 'Chill instrumental social media background music with mellow groove, soft keys, light percussion, and relaxed confidence. No vocals or lyrics.', icon: 'ri-sun-foggy-line' },
  { id: 'dramatic', label: 'Dramatic', prompt: 'Dramatic instrumental music cue with rising tension, bold percussion hits, and a strong final payoff for a short ad. No vocals or lyrics.', icon: 'ri-flashlight-line' },
  { id: 'custom', label: 'Custom', prompt: '', icon: 'ri-edit-2-line' },
];

function formatTime(value: number) {
  if (!Number.isFinite(value)) return '0:00.0';
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  const tenths = Math.floor((value % 1) * 10);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
}

export default function AudioStudioPage() {
  const location = useLocation();
  const initial = (location.state ?? {}) as AudioStudioState;
  const { history, loading } = useAdHistory(60);
  const { creditsRemaining, refetch: refetchCredits, fireToast } = useCreditsContext();

  const [videoUrl, setVideoUrl] = useState(initial.videoUrl ?? '');
  const [generationId, setGenerationId] = useState(initial.generationId ?? null);
  const [businessName, setBusinessName] = useState(initial.businessName ?? null);
  const [businessType, setBusinessType] = useState(initial.businessType ?? null);
  const [offer, setOffer] = useState(initial.offer ?? null);
  const [platform, setPlatform] = useState(initial.platform ?? null);
  const [audience, setAudience] = useState(initial.audience ?? null);
  const [tone, setTone] = useState(initial.tone ?? null);
  const [aspectRatio, setAspectRatio] = useState<VideoAspectRatio>(initial.aspectRatio ?? '9:16');

  const [audioUrl, setAudioUrl] = useState(initial.audioUrl ?? null);
  const [script, setScript] = useState(initial.voiceoverScript ?? '');
  const [audioMode, setAudioMode] = useState<AudioMode>(initial.voiceoverScript ? 'voiceover' : 'sound_effect');
  const [voiceStyle, setVoiceStyle] = useState<VoiceStyle>('friendly');
  const [soundStyle, setSoundStyle] = useState<SoundEffectStyle>('whoosh');
  const [soundPrompt, setSoundPrompt] = useState(soundEffectOptions[0].prompt);
  const [musicStyle, setMusicStyle] = useState<MusicStyle>('upbeat');
  const [musicPrompt, setMusicPrompt] = useState(musicOptions[0].prompt);
  const [duration, setDuration] = useState<VideoAdDuration>(5);

  const [videoDuration, setVideoDuration] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioStart, setAudioStart] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const videos = useMemo(() => history.flatMap(item =>
    (item.video_storage_urls ?? []).map((url, index) => ({
      id: `${item.id}-${index}`,
      generationId: item.id,
      url,
      audioUrl: item.video_audio_urls?.[index] ?? null,
      script: item.video_voiceover_scripts?.[index] ?? null,
      businessName: item.business_name,
      businessType: item.business_type,
      offer: item.offer,
      platform: item.platform,
      audience: item.audience,
      tone: item.tone,
      createdAt: item.created_at,
    }))
  ), [history]);

  useEffect(() => {
    if (audioUrl) {
      setTrimStart(0);
      setTrimEnd(audioDuration || duration);
    }
  }, [audioDuration, audioUrl, duration]);

  useEffect(() => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video || !audioUrl || !audio) return;

    const sync = () => {
      const clipDuration = Math.max(0, trimEnd - trimStart);
      const clipEnd = audioStart + clipDuration;
      const shouldPlay = !video.paused && video.currentTime >= audioStart && video.currentTime <= clipEnd;
      if (!shouldPlay) {
        audio.pause();
        return;
      }
      const target = trimStart + (video.currentTime - audioStart);
      if (Math.abs(audio.currentTime - target) > 0.18) audio.currentTime = Math.max(trimStart, Math.min(target, trimEnd));
      audio.play().catch(() => undefined);
    };

    const pause = () => audio.pause();
    video.addEventListener('timeupdate', sync);
    video.addEventListener('play', sync);
    video.addEventListener('pause', pause);
    video.addEventListener('seeked', sync);
    video.addEventListener('ended', pause);
    return () => {
      video.removeEventListener('timeupdate', sync);
      video.removeEventListener('play', sync);
      video.removeEventListener('pause', pause);
      video.removeEventListener('seeked', sync);
      video.removeEventListener('ended', pause);
    };
  }, [audioStart, audioUrl, trimEnd, trimStart]);

  const selectVideo = (item: typeof videos[number]) => {
    setVideoUrl(item.url);
    setGenerationId(item.generationId);
    setAudioUrl(item.audioUrl);
    setScript(item.script ?? '');
    setBusinessName(item.businessName);
    setBusinessType(item.businessType);
    setOffer(item.offer);
    setPlatform(item.platform);
    setAudience(item.audience);
    setTone(item.tone);
    setError(null);
  };

  const generateAudio = async () => {
    if (!videoUrl || generating) return;
    setGenerating(true);
    setError(null);
    try {
      const response = await fetch(AUDIO_EDGE_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({
          action: 'generate_audio',
          generationId,
          duration,
          sharedAdContext: {
            businessName,
            industry: businessType,
            offer,
            platform,
            targetAudience: audience,
            tone,
          },
          audioOptions: audioMode === 'voiceover'
            ? { mode: 'voiceover', voiceStyle, scriptMode: script.trim() ? 'custom' : 'auto', customScript: script.trim() || undefined }
            : audioMode === 'music'
              ? { mode: 'music', musicStyle, musicPrompt: musicPrompt.trim() || undefined }
              : { mode: 'sound_effect', soundEffectStyle: soundStyle, customSoundEffect: soundPrompt.trim() || undefined },
        }),
      });
      const data = await response.json() as { audioUrl?: string; voiceoverScript?: string; error?: string };
      if (!response.ok || data.error) throw new Error(data.error ?? `Audio generation failed (${response.status})`);
      if (!data.audioUrl) throw new Error('Audio generation did not return an audio file.');
      setAudioUrl(data.audioUrl);
      setScript(data.voiceoverScript ?? script);
      await refetchCredits();
      fireToast(audioMode === 'voiceover' ? 'Voiceover generated!' : audioMode === 'music' ? 'Music generated!' : 'Sound effect generated!', 'deduct', audioCost);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Audio generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const exportFinal = async () => {
    if (!videoUrl || !audioUrl || exporting) return;
    setExporting(true);
    setExportProgress(0);
    setError(null);
    try {
      await exportVideoWithOverlays({
        videoUrl,
        overlays: [],
        aspectRatio,
        audio: {
          url: audioUrl,
          startTime: audioStart,
          trimStart,
          trimEnd,
        },
        onProgress: setExportProgress,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed. Please try again.');
    } finally {
      setExporting(false);
      setExportProgress(0);
    }
  };

  const audioCost = audioMode === 'voiceover'
    ? VIDEO_AD_VOICEOVER_CREDITS
    : audioMode === 'music'
      ? VIDEO_AD_MUSIC_CREDITS
      : VIDEO_AD_SOUND_EFFECT_CREDITS;

  const audioLabel = audioMode === 'voiceover' ? 'Voiceover' : audioMode === 'music' ? 'Music' : 'Sound effect';

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-stone-950">Audio Studio</h1>
            <p className="text-sm text-stone-500 mt-1">Score existing videos with voiceover, sound effects, or music after you see the final visual.</p>
          </div>
          <div className="rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-bold text-stone-700">
            {creditsRemaining} credits
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)_360px] gap-6">
        <aside className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100">
            <h2 className="text-xs font-extrabold uppercase tracking-wider text-stone-800">Videos</h2>
          </div>
          <div className="max-h-[calc(100vh-190px)] overflow-y-auto p-3 space-y-2">
            {loading && <div className="h-20 rounded-xl bg-stone-100 animate-pulse" />}
            {!loading && videos.length === 0 && (
              <p className="text-sm text-stone-500 px-2 py-4">No saved videos found yet.</p>
            )}
            {videos.map(item => (
              <button
                key={item.id}
                onClick={() => selectVideo(item)}
                className={`w-full rounded-xl border p-2 text-left transition-all cursor-pointer ${
                  videoUrl === item.url
                    ? 'border-orange-300 bg-orange-50'
                    : 'border-stone-200 bg-stone-50 hover:border-orange-200'
                }`}
              >
                <video src={item.url} muted playsInline className="w-full rounded-lg bg-black aspect-video object-cover" />
                <div className="mt-2 text-xs font-bold text-stone-800 truncate">{item.businessName ?? 'Video ad'}</div>
                <div className="text-xs text-stone-400 truncate">{new Date(item.createdAt).toLocaleDateString()}</div>
              </button>
            ))}
          </div>
        </aside>

        <section className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-extrabold text-stone-900 uppercase tracking-wider">Preview</h2>
              <p className="text-sm text-stone-500 mt-1">{businessName ?? 'Select a video to begin'}</p>
            </div>
            <select
              value={aspectRatio}
              onChange={event => setAspectRatio(event.target.value as VideoAspectRatio)}
              className="h-10 rounded-xl border border-stone-200 bg-white px-3 text-xs font-bold text-stone-700"
            >
              <option value="9:16">9:16</option>
              <option value="1:1">1:1</option>
              <option value="16:9">16:9</option>
            </select>
          </div>
          <div className="p-5 space-y-4">
            {videoUrl ? (
              <div className="rounded-2xl bg-black overflow-hidden">
                <video
                  ref={videoRef}
                  src={videoUrl}
                  controls
                  playsInline
                  className="w-full max-h-[520px] object-contain"
                  onLoadedMetadata={event => {
                    const loaded = event.currentTarget.duration || 0;
                    setVideoDuration(loaded);
                    setDuration(loaded >= 8 ? 10 : 5);
                  }}
                />
              </div>
            ) : (
              <div className="h-[420px] rounded-2xl border-2 border-dashed border-stone-200 bg-stone-50 flex items-center justify-center text-stone-400">
                Select a saved video
              </div>
            )}
            {audioUrl && (
              <div className="rounded-xl border border-orange-100 bg-orange-50 p-3 space-y-3">
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  controls
                  className="w-full h-9"
                  onLoadedMetadata={event => setAudioDuration(event.currentTarget.duration || 0)}
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    ['Start', audioStart, 0, Math.max(videoDuration, 0), setAudioStart],
                    ['Trim In', trimStart, 0, Math.max(audioDuration, 0), setTrimStart],
                    ['Trim Out', trimEnd, 0, Math.max(audioDuration, 0), setTrimEnd],
                  ].map(([label, value, min, max, setter]) => (
                    <label key={String(label)}>
                      <span className="flex justify-between text-xs font-bold text-orange-800 mb-1">
                        {String(label)}
                        <span className="font-mono">{formatTime(Number(value))}</span>
                      </span>
                      <input
                        type="range"
                        min={Number(min)}
                        max={Number(max)}
                        step={0.1}
                        value={Number(value)}
                        onChange={event => (setter as (v: number) => void)(Number(event.target.value))}
                        className="w-full accent-orange-500 cursor-pointer"
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <aside className="rounded-2xl border border-stone-200 bg-white p-5 space-y-5 h-fit">
          <div>
            <h2 className="text-sm font-extrabold text-stone-900 uppercase tracking-wider">Audio</h2>
            <p className="text-sm text-stone-500 mt-1">Generate audio after watching the video.</p>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setAudioMode('sound_effect')}
              className={`rounded-xl border-2 px-3 py-3 text-xs font-bold transition-all cursor-pointer ${
                audioMode === 'sound_effect' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-stone-200 bg-stone-50 text-stone-500'
              }`}
            >
              <i className="ri-sound-module-line block text-base mb-1" />
              Sound Effect
            </button>
            <button
              onClick={() => setAudioMode('music')}
              className={`rounded-xl border-2 px-3 py-3 text-xs font-bold transition-all cursor-pointer ${
                audioMode === 'music' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-stone-200 bg-stone-50 text-stone-500'
              }`}
            >
              <i className="ri-music-2-line block text-base mb-1" />
              Music
            </button>
            <button
              onClick={() => setAudioMode('voiceover')}
              className={`rounded-xl border-2 px-3 py-3 text-xs font-bold transition-all cursor-pointer ${
                audioMode === 'voiceover' ? 'border-orange-400 bg-orange-50 text-orange-700' : 'border-stone-200 bg-stone-50 text-stone-500'
              }`}
            >
              <i className="ri-mic-line block text-base mb-1" />
              Voiceover
            </button>
          </div>

          {audioMode === 'voiceover' ? (
            <div className="space-y-3">
              <select
                value={voiceStyle}
                onChange={event => setVoiceStyle(event.target.value as VoiceStyle)}
                className="w-full h-11 rounded-xl border border-stone-200 bg-white px-3 text-sm font-bold text-stone-800"
              >
                <option value="friendly">Friendly</option>
                <option value="energetic">Energetic</option>
                <option value="luxury">Luxury</option>
                <option value="calm">Calm</option>
                <option value="bold">Bold</option>
              </select>
              <textarea
                value={script}
                onChange={event => setScript(event.target.value.slice(0, 260))}
                rows={5}
                placeholder="Write or edit the voiceover script..."
                className="w-full resize-none rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400"
              />
            </div>
          ) : audioMode === 'music' ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {musicOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setMusicStyle(option.id);
                      if (option.id !== 'custom') setMusicPrompt(option.prompt);
                    }}
                    className={`rounded-xl border px-2 py-2 text-xs font-bold transition-all cursor-pointer ${
                      musicStyle === option.id ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-stone-200 bg-stone-50 text-stone-500'
                    }`}
                  >
                    <i className={`${option.icon} block text-sm mb-1`} />
                    {option.label}
                  </button>
                ))}
              </div>
              <textarea
                value={musicPrompt}
                onChange={event => {
                  setMusicPrompt(event.target.value.slice(0, 420));
                  setMusicStyle('custom');
                }}
                rows={5}
                placeholder="Describe the background music..."
                className="w-full resize-none rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400"
              />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {soundEffectOptions.map(option => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSoundStyle(option.id);
                      if (option.id !== 'custom') setSoundPrompt(option.prompt);
                    }}
                    className={`rounded-xl border px-2 py-2 text-xs font-bold transition-all cursor-pointer ${
                      soundStyle === option.id ? 'border-orange-300 bg-orange-50 text-orange-700' : 'border-stone-200 bg-stone-50 text-stone-500'
                    }`}
                  >
                    <i className={`${option.icon} block text-sm mb-1`} />
                    {option.label}
                  </button>
                ))}
              </div>
              <textarea
                value={soundPrompt}
                onChange={event => {
                  setSoundPrompt(event.target.value.slice(0, 220));
                  setSoundStyle('custom');
                }}
                rows={4}
                className="w-full resize-none rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400"
              />
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
          )}

          <div className="rounded-xl bg-stone-900 px-4 py-3 text-sm">
            <div className="flex justify-between text-stone-400">
              <span>{audioLabel}</span>
              <span className="font-bold text-stone-100">{audioCost} credits</span>
            </div>
          </div>

          <button
            onClick={generateAudio}
            disabled={!videoUrl || generating || creditsRemaining < audioCost}
            className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-extrabold transition-all ${
              videoUrl && !generating && creditsRemaining >= audioCost
                ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            <i className={generating ? 'ri-loader-4-line animate-spin' : audioMode === 'voiceover' ? 'ri-mic-2-line' : audioMode === 'music' ? 'ri-music-2-line' : 'ri-sound-module-line'} />
            {generating ? 'Generating...' : 'Generate Audio'}
          </button>

          <button
            onClick={exportFinal}
            disabled={!videoUrl || !audioUrl || exporting}
            className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-sm font-extrabold transition-all ${
              videoUrl && audioUrl && !exporting
                ? 'bg-stone-900 text-white hover:bg-stone-800 cursor-pointer'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            <i className={exporting ? 'ri-loader-4-line animate-spin' : 'ri-export-line'} />
            {exporting ? `Exporting ${Math.round(exportProgress * 100)}%` : 'Export Video With Audio'}
          </button>

          {audioUrl && (
            <button
              onClick={() => downloadFile(audioUrl, `adforge-audio-${Date.now()}.mp3`)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm font-bold text-stone-700 hover:border-orange-200 hover:text-orange-600"
            >
              <i className="ri-download-2-line" />
              Download MP3
            </button>
          )}
        </aside>
      </main>
    </div>
  );
}
